import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, resolveAssetUrl, pickLocalized } from "@/contexts/SiteContentContext";
import { useDomain } from "@/hooks/useDomain";
import LiveSpeedChip from "@/components/LiveSpeedChip";
import { ArrowRight, MessageCircle, Wifi, Satellite, Sparkles, ShieldCheck } from "lucide-react";

const pickDomainOrCms = (domainVal, cmsVal, locale, fallback) => {
  if (domainVal && domainVal[locale]) return domainVal[locale];
  if (domainVal && domainVal.de) return domainVal.de;
  return pickLocalized(cmsVal, locale, fallback);
};

const FloatingChip = ({ icon: Icon, label, value, className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8, ease: "easeOut" }}
    className={`absolute hidden md:flex glass rounded-2xl px-4 py-3 items-center gap-3 shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${className}`}
  >
    <div className="w-9 h-9 rounded-xl bg-sky-600 text-white grid place-items-center">
      <Icon className="w-4 h-4" />
    </div>
    <div className="leading-tight">
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
        {label}
      </div>
      <div className="text-sm font-medium text-neutral-900">{value}</div>
    </div>
  </motion.div>
);

const Hero = () => {
  const { locale, t } = useLanguage();
  const { content } = useSiteContent();
  const domain = useDomain();
  const h = content?.hero || {};
  const phone = (content?.contact?.whatsapp_number || "+34657138915").replace(/[^+\d]/g, "");

  const bg = resolveAssetUrl(h.background_url);
  const dHero = domain.hero || {};
  const eyebrow = pickDomainOrCms(dHero.eyebrow, h.eyebrow, locale, "Premium Tech Concierge · Mallorca");
  const title = pickDomainOrCms(dHero.title, h.title, locale, "Digitale & technische Lösungen für Deutsche auf Mallorca");
  const subtitle = pickDomainOrCms(dHero.subtitle, h.subtitle, locale, "");
  const cta1 = pickDomainOrCms(dHero.cta1, h.cta_primary, locale, "Kostenlos beraten lassen");
  const cta2 = pickLocalized(h.cta_whatsapp, locale, "WhatsApp Kontakt");
  const ctaTarget = domain.focusedServiceSlug ? `#service-focus` : "#contact";

  return (
    <section id="hero" data-testid="hero-section" className="relative pt-32 lg:pt-36 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        {bg && (
          <img
            src={bg}
            alt="Premium Mallorca villa"
            className="w-full h-full object-cover"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/15 to-[#fdfbf7]" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/40 to-transparent" />
      </div>

      <div className="container-px mx-auto max-w-7xl pb-28 lg:pb-40 pt-10 lg:pt-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-white/60 px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-sky-700 mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            {eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-neutral-950 tracking-tighter leading-[1.02] text-balance"
          >
            {title}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-lg lg:text-xl text-neutral-700 max-w-2xl leading-relaxed"
          >
            {subtitle}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={ctaTarget}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-6 py-4 text-sm font-medium hover:bg-black transition-colors"
            >
              {cta1}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href={`https://wa.me/${phone.replace(/^\+/, "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 text-neutral-900 px-6 py-4 text-sm font-medium hover:bg-white hover:shadow-md transition-all"
            >
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              {cta2}
            </a>
          </div>

          <div className="mt-12 flex items-center gap-6 text-xs text-neutral-600">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-700" /> {t("hero.trust1")}</div>
            <div className="flex items-center gap-2"><Satellite className="w-4 h-4 text-sky-700" /> {t("hero.trust2")}</div>
            <div className="hidden sm:flex items-center gap-2"><Wifi className="w-4 h-4 text-sky-700" /> {t("hero.trust3")}</div>
          </div>
        </motion.div>

        <div className="absolute inset-0 pointer-events-none">
          <LiveSpeedChip className="top-24 right-8 lg:right-16" delay={0.3} />
          <FloatingChip icon={Wifi} label="Mesh" value="3 Nodes · Online" className="top-[55%] right-24 lg:right-44" delay={0.5} />
          <FloatingChip icon={ShieldCheck} label="Smart Home" value="Villa armed" className="bottom-10 right-10 lg:right-32 animate-float" delay={0.7} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
